import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useCurrentStudy } from "@/context/CurrentStudyContext";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  Gauge, TrendingUp, AlertTriangle, Trophy, Frown,
  Truck, Award, Pill, Layers, Calendar, Loader2, Share2,
  PackageX, PackageOpen, Sparkles, FileSpreadsheet, FileText, Mail, Download,
  ChevronLeft, ChevronRight, Target, TrendingDown,
} from "lucide-react";
import { downloadXlsx } from "@/lib/download";
import { logClientError } from "@/lib/errorLogger";

const fmtEur = (n) => Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const fmtNum = (n) => Number(n || 0).toLocaleString("es-ES");
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

const COLORS = {
  primary: "#0F172A",
  emerald: "#10B981",
  sky: "#0EA5E9",
  rose: "#E11D48",
  amber: "#F59E0B",
  slate: "#64748B",
  violet: "#8B5CF6",
};
const PIE_COLORS = ["#10B981", "#0EA5E9", "#F59E0B", "#8B5CF6", "#E11D48", "#0F172A", "#64748B", "#22C55E", "#3B82F6", "#EAB308"];

function safeStr(v, fb = "") {
  if (v == null) return fb;
  if (typeof v === "string" || typeof v === "number") return v;
  return fb;
}

/**
 * Error boundary local: aísla crashes de los sub-componentes del dashboard
 * (tarjetas, gráficas Recharts, etc.) para que un fallo puntual no deje al
 * usuario con pantalla en blanco. Muestra un mensaje neutro con opción de
 * recargar la página.
 */
class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("Dashboard crash:", error, info);
    // Reportar al backend (best-effort, no bloqueante).
    logClientError(error, { component_stack: info?.componentStack || "" });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div
          className="bg-rose-50 border border-rose-200 rounded-2xl p-8 text-center"
          data-testid="dashboard-crash-fallback"
        >
          <div className="text-rose-900 font-semibold mb-2">
            No se ha podido pintar el cuadro de mando.
          </div>
          <div className="text-sm text-rose-800 mb-4">
            Se ha producido un error inesperado al renderizar los datos del estudio. Nuestro equipo ya está avisado.
            Prueba a <strong>recargar la página</strong> (Ctrl+F5) o a seleccionar otro estudio del desplegable superior.
          </div>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-md text-sm font-semibold"
            data-testid="dashboard-crash-reload"
          >
            Recargar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function CuadroMandoPage() {
  const [studies, setStudies] = useState([]);
  const [dispId, setDispId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { current, setCurrent } = useCurrentStudy();

  useEffect(() => {
    api.get("/dispensations").then((r) => {
      const list = r.data || [];
      setStudies(list);
      if (current?.id && list.find((s) => s.id === current.id)) {
        setDispId(current.id);
      } else if (list.length) {
        setDispId(list[0].id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  const handleChangeStudy = (id) => {
    setDispId(id);
    setCurrent(id);
  };

  useEffect(() => {
    if (!dispId) return;
    setLoading(true);
    api.get("/dashboard/executive", { params: { disp_id: dispId } })
      .then((r) => setData(r.data))
      .catch((e) => {
        const status = e?.response?.status;
        // Si el disp_id guardado en el contexto ya no existe (404), lo
        // limpiamos y forzamos a coger el primero disponible. Evita que
        // el usuario se quede en pantalla en blanco tras borrar estudios.
        if (status === 404 && studies.length > 0 && studies[0]?.id !== dispId) {
          setDispId(studies[0].id);
          setCurrent(studies[0].id);
          return;
        }
        toast.error(e?.response?.data?.detail || "Error al cargar el cuadro de mando");
      })
      .finally(() => setLoading(false));
  }, [dispId]);

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 max-w-[1700px]" data-testid="cuadro-mando-page">
      {/* Hero · vídeo cinematográfico */}
      <div className="relative mb-6 sm:mb-10 rounded-2xl overflow-hidden bg-slate-950 shadow-xl border border-slate-200" data-testid="cuadromando-hero-video">
        <video
          src="/video/farmaclass-cuadromando.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-auto max-h-[200px] sm:max-h-[300px] lg:max-h-[420px] object-cover block"
          aria-hidden="true"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/50 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center px-4 sm:px-8 lg:px-12 max-w-2xl pointer-events-none">
          <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] font-bold text-sky-300 mb-1 sm:mb-2 inline-flex items-center gap-2">
            <Gauge className="h-3 w-3" /> FarmaClass · Cuadro de mando
          </div>
          <h2 className="font-display text-lg sm:text-2xl lg:text-3xl font-extrabold tracking-tight text-white leading-tight">
            Tu farmacia, en una sola pantalla.
          </h2>
          <p className="hidden sm:block text-sm text-white/70 mt-2 max-w-lg">
            KPIs, semáforo de dispensación, top moléculas y proveedores — todo lo que necesitas para decidir el próximo movimiento.
          </p>
        </div>
      </div>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3 inline-flex items-center gap-2">
            <Gauge className="h-3.5 w-3.5" /> Cuadro de mando ejecutivo
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tighter text-slate-950 break-words">
            {safeStr(data?.dispensation?.label, "Cuadro de mando")}
          </h1>
          {data?.dispensation && (
            <p className="text-slate-500 mt-2 text-xs sm:text-sm inline-flex items-center gap-2 flex-wrap">
              <Calendar className="h-3.5 w-3.5" />
              {safeStr(data.dispensation.period_start)} → {safeStr(data.dispensation.period_end)} · {safeStr(data.dispensation.months)} mes{Number(data.dispensation.months) !== 1 ? "es" : ""}
            </p>
          )}
        </div>
        <div className="flex items-end gap-3 w-full sm:w-auto">
          <div className="w-full sm:w-auto">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Estudio de referencia</div>
            <select
              value={dispId || ""}
              onChange={(e) => handleChangeStudy(e.target.value)}
              data-testid="select-study"
              className="w-full sm:w-auto px-3 py-2 border border-slate-300 rounded-md text-sm bg-white sm:min-w-[240px]"
            >
              {studies.map((s) => (
                <option key={s.id} value={s.id}>{safeStr(s.label || s.filename, s.id)}</option>
              ))}
            </select>
          </div>
        </div>
      </header>

      {loading || !data ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center text-slate-500">
          <Loader2 className="h-7 w-7 animate-spin mx-auto mb-3" />
          Cargando datos…
        </div>
      ) : (
        <DashboardErrorBoundary>
          <MedalBanner />
          <Dashboard data={data} studies={studies} dispId={dispId} onSelectStudy={handleChangeStudy} />
        </DashboardErrorBoundary>
      )}
    </div>
  );
}

function MedalBanner() {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    api.get("/benchmark/provinces").then((r) => setPos(r.data?.self_position)).catch(() => {});
  }, []);
  if (!pos || pos.ccaa_percentile == null) return null;
  const p = pos.ccaa_percentile;
  const medal =
    p >= 90 ? { emoji: "\u{1F947}", label: "Top 10%", tone: "from-amber-400 to-yellow-500", textTone: "text-amber-900" } :
    p >= 75 ? { emoji: "\u{1F948}", label: "Top 25%", tone: "from-slate-300 to-slate-400", textTone: "text-slate-900" } :
    p >= 50 ? { emoji: "\u{1F949}", label: "Mejor que la media", tone: "from-orange-300 to-amber-400", textTone: "text-amber-900" } :
    null;
  if (!medal) return null;
  const ccaaStr = typeof pos.ccaa === "string" ? pos.ccaa : "";
  const rankStr = typeof pos.ccaa_rank === "number" ? pos.ccaa_rank : "?";
  const totalStr = typeof pos.ccaa_studies === "number" ? pos.ccaa_studies : "?";
  const msg = encodeURIComponent(
    `Mi farmacia est\u00e1 en ${medal.label} en ${ccaaStr} (percentil ${Math.round(p)}) en dispensaci\u00f3n EFG. Mira el ranking \u2192 https://farmaclass.es`,
  );
  return (
    <div
      data-testid="medal-banner"
      className={`mb-6 rounded-2xl p-4 lg:p-5 bg-gradient-to-r ${medal.tone} ${medal.textTone} flex flex-wrap items-center gap-4 shadow-md`}
    >
      <span style={{ fontSize: 38, lineHeight: 1 }} aria-hidden>{medal.emoji}</span>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-80">FarmaClass Benchmark</div>
        <div className="font-display text-xl lg:text-2xl font-bold leading-tight">
          ¡Tu farmacia es {medal.label} en {pos.ccaa}!
        </div>
        <div className="text-sm opacity-80 mt-0.5">
          Posición #{pos.ccaa_rank} de {pos.ccaa_studies} · percentil {Math.round(p)}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href={`https://wa.me/?text=${msg}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-whatsapp-btn"
          className="inline-flex items-center gap-1.5 bg-slate-950 text-white text-sm font-bold px-4 py-2.5 rounded-md hover:bg-slate-800 transition-colors"
        >
          <Share2 className="h-4 w-4" />
          Compartir en WhatsApp
        </a>
        <a
          href="/benchmark"
          className="inline-flex items-center text-xs font-bold opacity-80 hover:opacity-100 underline"
        >
          Ver más
        </a>
      </div>
    </div>
  );
}

function StudyNarrativeCard({ k, timeline = [] }) {
  const eurMarca = k?.eur_marca || 0;
  const eurGen = k?.eur_generico || 0;
  const curIdx = timeline.findIndex((t) => t.is_current);
  const prev = curIdx > 0 ? timeline[curIdx - 1] : (timeline.length >= 2 ? timeline[timeline.length - 2] : null);
  const current = curIdx >= 0 ? timeline[curIdx] : (timeline[timeline.length - 1] || null);

  // Proyección mensual (equivalente a 30 días) para no comparar "manzanas
  // con peras" cuando el estudio actual solo cubre 10 días vs el anterior de 30.
  const curDays = current?.days || 30;
  const prevDays = prev?.days || 30;
  const curFactor = curDays > 0 ? 30 / curDays : 1;
  const prevFactor = prevDays > 0 ? 30 / prevDays : 1;

  const projGenCur = eurGen * curFactor;
  const projMarcaCur = eurMarca * curFactor;
  const projGenPrev = (prev?.eur_generico || 0) * prevFactor;

  const deltaEurMonthly = prev && current ? (projGenCur - projGenPrev) : null;
  const deltaPct = prev && current ? ((current.pct_eur || 0) - (prev.pct_eur || 0)) : null;

  const isPartial = current?.is_partial;

  const improveTxt = () => {
    if (deltaEurMonthly == null || deltaPct == null || !prev) {
      return "Sube un segundo estudio para poder comparar tu evolución con el mes anterior.";
    }
    const sign = deltaEurMonthly >= 0 ? "+" : "−";
    const absEur = Math.abs(deltaEurMonthly);
    const sign2 = deltaPct >= 0 ? "+" : "−";
    const absPct = Math.abs(deltaPct);
    const color = (deltaEurMonthly >= 0 || deltaPct >= 0) ? "text-emerald-300" : "text-rose-300";
    return (
      <>
        Frente a <strong>{prev.label}</strong>: <span className={`font-semibold ${color}`}>{sign}{fmtEur(absEur)}</span> en genérico y <span className={`font-semibold ${color}`}>{sign2}{absPct.toFixed(1)} pp</span> de cuota de genérico.
      </>
    );
  };

  return (
    <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-2xl p-5 mb-6 shadow-sm" data-testid="study-narrative-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-amber-300">Resumen del estudio</div>
        {isPartial && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-300 bg-amber-500/20 px-2 py-0.5 rounded-full">
            Período parcial · proyecciones a 30 d
          </span>
        )}
      </div>
      <div className="space-y-3 text-sm sm:text-[15px] leading-relaxed">
        {/* Bloque 1 · Marca (oportunidad) */}
        <div>
          <div>
            {isPartial ? (
              <>En estos <strong>{curDays} días</strong> has dispensado <strong className="text-rose-300">{fmtEur(eurMarca)}</strong> en marca que podrías haber convertido a genérico.</>
            ) : (
              <>Este período has dispensado <strong className="text-rose-300">{fmtEur(eurMarca)}</strong> en marca que podrías haber convertido a genérico.</>
            )}
          </div>
          {isPartial && (
            <div className="text-slate-300 text-sm">
              A este ritmo, cerrarás el mes con <strong className="text-rose-300">{fmtEur(projMarcaCur)}</strong> de oportunidad.
            </div>
          )}
        </div>

        {/* Bloque 2 · Genérico ya conseguido */}
        <div>
          <div>
            Ya has generado <strong className="text-emerald-300">{fmtEur(eurGen)}</strong> en genérico.
          </div>
          {isPartial && (
            <div className="text-slate-300 text-sm">
              Proyección a cierre de mes: <strong className="text-emerald-300">{fmtEur(projGenCur)}</strong>.
            </div>
          )}
        </div>

        {/* Bloque 3 · Delta vs estudio anterior */}
        <div className="text-slate-200 pt-1">
          {improveTxt()}
        </div>
      </div>
    </div>
  );
}

function Dashboard({ data, studies = [], dispId = null, onSelectStudy = null }) {
  // Null-safety defensiva: aunque el backend garantiza estos campos,
  // un 5xx parcial, un timeout intermitente o una regresión pueden
  // devolver `data` sin alguna clave y hacer que el componente crashee
  // en runtime → pantalla en blanco. Preferimos degradar a "vacío".
  const k = data?.kpis || {};
  const split = data?.purchases_split || { with_agreement_eur: 0, without_agreement_eur: 0, total_eur: 0, with_agreement_pct: 0 };
  const emp = data?.employees;
  const inc = data?.incentives;
  const tl = data?.timeline || [];

  // Donut compra con/sin condición
  const purchaseSplitData = useMemo(() => [
    { name: "Con condición", value: split.with_agreement_eur || 0, color: COLORS.emerald },
    { name: "Sin condición", value: split.without_agreement_eur || 0, color: COLORS.rose },
  ], [split]);

  return (
    <>
      {/* Resumen narrativo del estudio (nueva tarjeta arriba del todo) */}
      <StudyNarrativeCard k={k} timeline={tl} />

      {/* HERO: Mejora con Archiduque (si hay) */}
      <ArchiduqueBenefitCard
        benefit={data.archiduque_benefit}
        studies={studies}
        currentDispId={dispId}
        onSelect={onSelectStudy}
      />

      {/* TOP KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-6">
        <KpiCard
          icon={Layers}
          accent="indigo"
          label="Volumen EFG total (€)"
          value={fmtEur(k.eur_total || 0)}
          sub={`${fmtEur(k.eur_generico || 0)} gen · ${fmtEur(k.eur_marca || 0)} marca`}
          hint="Volumen total de POSIBILIDADES de genérico en euros: suma del € dispensado en marca + genérico dentro del universo EFG del estudio. Es el pastel completo que podrías capturar 100% en genérico."
          href={data.dispensation?.id ? `/dispensations/${data.dispensation.id}?tab=overview` : "/history"}
          testid="kpi-eur-total-efg"
        />
        <KpiCard
          icon={Layers}
          accent="indigo"
          label="G.H. distintos"
          value={fmtNum(k.total_gh || 0)}
          sub={`${fmtNum(k.gh_only_generico || 0)} sólo gen · ${fmtNum(k.gh_only_marca || 0)} sólo marca · ${fmtNum(k.gh_mixed || 0)} mixtas`}
          hint="Número de conjuntos homogéneos (G.H.) diferentes con al menos 1 unidad dispensada en este estudio."
          href={data.dispensation?.id ? `/dispensations/${data.dispensation.id}?tab=molecules` : "/history"}
          testid="kpi-gh-distintos"
        />
        <KpiCard icon={Pill} accent="emerald" label="% Genérico (uds)" value={fmtPct(k.pct_gen_units)} sub={`${fmtNum(k.units_total)} uds totales`} href={data.dispensation?.id ? `/dispensations/${data.dispensation.id}?tab=overview&sort=units&dir=desc` : "/history"} testid="kpi-pct-gen-uds" />
        <KpiCard icon={Pill} accent="sky" label="% Genérico (€)" value={fmtPct(k.pct_gen_eur)} sub={fmtEur(k.eur_total)} href={data.dispensation?.id ? `/dispensations/${data.dispensation.id}?tab=overview&sort=euros&dir=desc` : "/history"} testid="kpi-pct-gen-eur" />
        <KpiCard
          icon={Award}
          accent="amber"
          label="Incentivos del mes"
          value={fmtEur(inc.total_eur)}
          sub={
            (inc.months || 0) > 0
              ? `${inc.month_label || "mes"} · ${inc.molecules_ok || 0} con premio${(inc.molecules_no_cobra || 0) ? ` · ${inc.molecules_no_cobra} no cobra` : ""}`
              : "Sube otro estudio para empezar a calcular"
          }
          hint={
            (inc.months || 0) > 0
              ? `Premio SOLO del mes ${inc.month_label || ""} (mismo cálculo que el módulo Incentivos). Acumulado desde el baseline: ${fmtEur(inc.accumulated_eur || 0)} en ${inc.months} mes(es).`
              : "Premio del mes según el módulo Incentivos. Sólo cuenta crecimiento >0 vs baseline."
          }
          href={(inc.months || 0) > 0 ? "/incentives" : null}
          testid="kpi-incentivos"
        />
        <KpiCard icon={Pill} accent="rose" label="€ Marca total" value={fmtEur(k.eur_marca || 0)} sub={`${(((k.eur_marca || 0) / (k.eur_total || 1)) * 100).toFixed(1)}% del total`} hint="Importe total dispensado en producto de marca durante el estudio. Pulsa para ver las moléculas ordenadas por donde más € pierdes en marca." href={data.dispensation?.id ? `/dispensations/${data.dispensation.id}?tab=marca&sort=euros&dir=desc` : "/history"} testid="kpi-eur-marca" />
        <KpiCard icon={Pill} accent="emerald" label="€ Genérico total" value={fmtEur(k.eur_generico || 0)} sub={`${(((k.eur_generico || 0) / (k.eur_total || 1)) * 100).toFixed(1)}% del total`} hint="Importe total dispensado en producto genérico (EFG) durante el estudio. Pulsa para ver el estudio completo ordenado por donde más dispensas." href={data.dispensation?.id ? `/dispensations/${data.dispensation.id}?tab=overview&sort=euros&dir=desc` : "/history"} testid="kpi-eur-generico" />
      </div>

      {/* Resumen de posición en Benchmark (siempre visible) */}
      <BenchmarkSummaryCard />

      {/* Row 1: Evolución + Distribución compras */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2" testid="chart-evolution" href="/comparison" hrefLabel="Ver comparativa entre estudios">
          <CardTitle icon={TrendingUp}>Evolución de la dispensación</CardTitle>
          <CardSubtitle>% genérico en unidades (sólido) y € (discontinuo) a lo largo del tiempo</CardSubtitle>
          {tl.length < 1 ? (
            <EmptyChart>Sin datos suficientes</EmptyChart>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={tl} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
                  <YAxis stroke="#64748b" fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="pct_units" name="% Gen. Unidades" stroke={COLORS.emerald} strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="pct_eur" name="% Gen. Euros" stroke={COLORS.sky} strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
              {/* Beneficio acumulado vs primer estudio */}
              {tl.length >= 2 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">
                    Beneficio por estudio vs {tl[0]?.label || "inicial"} (delta % genérico × total × margen)
                  </div>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={tl.slice(1)} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" stroke="#64748b" fontSize={10} />
                      <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => `${v}€`} />
                      <Tooltip formatter={(v) => fmtEur(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="benefit_vs_baseline_eur" name="Beneficio €" fill={COLORS.emerald} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </Card>

        <Card testid="chart-purchase-split" href={data.dispensation?.id ? `/dispensations/${data.dispensation.id}?tab=suppliers` : "/proveedores"} hrefLabel="Ver detalle de proveedores en el estudio">
          <CardTitle icon={Truck}>Distribución de compras</CardTitle>
          <CardSubtitle>Por condición comercial · ¿comprando donde tienes acuerdo?</CardSubtitle>
          <div className="flex items-center justify-center mt-2">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={purchaseSplitData}
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {purchaseSplitData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtEur(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-2">
            <LegendRow color={COLORS.emerald} label="Con condición" value={fmtEur(split.with_agreement_eur)} pct={split.with_agreement_pct} />
            <LegendRow color={COLORS.rose} label="Sin condición" value={fmtEur(split.without_agreement_eur)} pct={(100 - (split.with_agreement_pct || 0)).toFixed(1)} />
          </div>
        </Card>
      </div>

      {/* Row 1.5: Stock gap / Solo Marca */}
      <StockGapCard gap={data.stock_gap} totalMarcaEur={k.eur_marca || 0} dispId={data.dispensation?.id} dispLabel={data.dispensation?.label} />

      {/* Row 1.6: Stock muerto (CN con stock que no se han dispensado) */}
      <StockDeadCard dead={data.stock_dead} dispId={data.dispensation?.id} />

      {/* Row 1.65: Moléculas en caída vs el estudio anterior (iter-98) */}
      <MoleculesFallingCard falling={data.molecules_falling} dispId={data.dispensation?.id} />

      {/* Row 1.7: PENDIENTE DE CONVERTIR — % de marca aún en solo_marca + nº de moléculas */}
      <PendingToConvertCard gap={data.stock_gap} dispLabel={data.dispensation?.label} dispId={data.dispensation?.id} />

      {/* Row 1.7: Resumen Top 25 Marca (pérdida estimada del mes) */}
      {data.top_25_brand_summary && data.top_25_brand_summary.marca_euros > 0 && (
        <a
          href={data.dispensation?.id ? `/dispensations/${data.dispensation.id}?tab=top` : "/pedido/falta"}
          className="mb-6 bg-slate-950 text-white rounded-2xl overflow-hidden relative block transition-all duration-200 hover:shadow-2xl hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-rose-400"
          data-testid="top-25-brand-summary"
          title="Ver el detalle del Top 25 (€, marca, dispensaciones totales)"
        >
          <div className="absolute top-4 left-6 text-[10px] uppercase tracking-[0.28em] font-bold text-white/40">
            FarmaClass · Top 25 marca · €
          </div>
          <div className="absolute top-4 right-6 text-[11px] uppercase tracking-wider font-bold text-amber-300/90 inline-flex items-center gap-1">
            Ver detalle Top 25 <span aria-hidden>→</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4 sm:px-6 py-10 sm:py-12 md:py-14 pt-12 sm:pt-14">
            {/* Pérdida estimada */}
            <div>
              <div className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-1">Pérdida estimada del mes</div>
              <div className="text-[11px] sm:text-sm text-white/60 mb-4 sm:mb-6">
                25 moléculas con más € marca · {fmtNum(data.top_25_brand_summary.marca_units)} uds
                {data.dispensation?.label ? ` · ${data.dispensation.label}` : ""}
              </div>
              <div className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-rose-400 font-mono-num leading-none mb-4 break-words">
                {fmtEur(data.top_25_brand_summary.marca_euros)}
              </div>
              <div className="text-xs text-white/60 max-w-md leading-relaxed">
                Si convertimos estas 25 moléculas a genérico, recuperaríamos casi todo este importe.
              </div>
              {/* Iter-80: Top 5 REAL del Top 25 para que coincida con la vista de detalle */}
              {(data.top_25_brand_summary.top_5 || []).length > 0 && (
                <div className="mt-6 border-t border-white/10 pt-4" data-testid="top-25-brand-top5">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">
                    Las 5 con más € marca del estudio
                  </div>
                  <ol className="space-y-1.5">
                    {(data.top_25_brand_summary.top_5 || []).map((m, i) => (
                      <li key={m.gh} className="flex items-start gap-3 text-xs" data-testid={`top-25-brand-top5-row-${i}`}>
                        <span className="text-rose-300 font-bold w-4 flex-shrink-0">#{i + 1}</span>
                        <span className="flex-1 min-w-0 text-white/90 truncate" title={m.gh}>{m.gh}</span>
                        <span className="font-mono-num font-bold text-rose-300 whitespace-nowrap">{fmtEur(m.marca_euros)}</span>
                        <span className="font-mono-num text-white/50 whitespace-nowrap">{fmtNum(m.marca_units)} u</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>

            {/* Impacto sobre total marca */}
            <div className="md:border-l md:border-white/10 md:pl-6 flex flex-col justify-center">
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-amber-300 mb-2">Impacto sobre el total marca</div>
              <div className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold text-amber-300 font-mono-num leading-none mb-3">
                {(data.top_25_brand_summary.pct_over_total_marca || 0).toFixed(1)}%
              </div>
              <div className="text-xs text-white/60 max-w-sm">
                25 moléculas concentran el <strong className="text-white">{(data.top_25_brand_summary.pct_over_total_marca || 0).toFixed(1)}%</strong> del coste total de marca.
              </div>
            </div>
          </div>
        </a>
      )}

      {/* Row 1.8 (eliminada — el usuario lo quiere solo dentro del estudio) */}

      {/* Row 2: Empleados + Top losses (el cuadro "Top 5 atípicas" se eliminó por
          petición del usuario — iter-95; sigue disponible dentro del estudio) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card testid="card-employees" href="/empleados/analisis" hrefLabel="Ver análisis por empleado">
          <CardTitle icon={Trophy}>Empleados</CardTitle>
          <CardSubtitle>Mejor y peor empleado por % genérico en euros</CardSubtitle>
          {!emp ? (
            <EmptyChart>Sube un estudio con desglose por empleado para verlos aquí.</EmptyChart>
          ) : (
            <div className="space-y-3 mt-3">
              <EmployeeCard variant="best" emp={emp.best} />
              <EmployeeCard variant="worst" emp={emp.worst} />
              <div className="text-[11px] text-slate-500 text-center pt-1">{emp.count} empleados en el equipo</div>
            </div>
          )}
        </Card>

        <Card testid="card-top-losses" href={data.dispensation?.id ? `/dispensations/${data.dispensation.id}?tab=top` : "/pedido/falta"} hrefLabel="Ver Top 25 en el estudio">
          <CardTitle icon={Layers}>Top 5 moléculas con más € en marca</CardTitle>
          <CardSubtitle>Coinciden con las primeras 5 del Top 25 del estudio</CardSubtitle>
          {!data.top_losses?.length ? (
            <EmptyChart>Sin pérdidas detectadas. ¡Buen trabajo!</EmptyChart>
          ) : (
            <div className="space-y-2 mt-3">
              {data.top_losses.map((l, idx) => (
                <div key={l.gh} className="bg-rose-50/40 border border-rose-100 rounded-lg p-3" data-testid={`top-loss-${idx}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700 flex items-center gap-1.5">
                        <span className="font-mono-num bg-rose-100 px-1.5 rounded-full">#{idx + 1}</span>
                        {l.state_label || (l.state_key === "marginal" ? "Marginal" : l.state_key === "solo_marca" ? "Solo Marca" : l.state_key === "mixta" ? "Mixta" : "Marca")}
                        {l.preferred_lab && (
                          <span className="text-[9px] text-slate-500 normal-case font-normal">· → {l.preferred_lab}</span>
                        )}
                      </div>
                      <div className="text-sm font-medium text-slate-900 truncate" title={l.gh}>{l.gh}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-lg font-bold text-rose-700 font-mono-num whitespace-nowrap">{fmtEur(l.euros_marca)}</div>
                      <div className="text-[11px] text-slate-500 font-mono-num">{fmtNum(l.units_marca)} uds</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

      </div>

      {/* Row 3: Suppliers bar chart */}
      <Card testid="chart-suppliers" href={data.dispensation?.id ? `/dispensations/${data.dispensation.id}?tab=suppliers` : "/proveedores"} hrefLabel="Ver todos los proveedores del estudio">
        <CardTitle icon={Truck}>Compras por proveedor</CardTitle>
        <CardSubtitle>Barras verdes = con condición de compra · Barras rosadas = sin condición. Pulsa una barra para abrir ese proveedor en el estudio.</CardSubtitle>
        {!data.suppliers_by_agreement?.length ? (
          <EmptyChart>Sin proveedores en el estudio</EmptyChart>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(260, data.suppliers_by_agreement.length * 32)}>
            <BarChart
              data={data.suppliers_by_agreement.slice(0, 20)}
              layout="vertical"
              margin={{ top: 8, right: 32, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" stroke="#64748b" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
              <YAxis type="category" dataKey="laboratorio" width={160} stroke="#64748b" fontSize={11} interval={0} />
              <Tooltip formatter={(v) => fmtEur(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Bar
                dataKey="euros"
                name="€ compras"
                cursor="pointer"
                onClick={(payload) => {
                  const lab = payload?.laboratorio || payload?.payload?.laboratorio;
                  if (!lab) return;
                  const dispId = data.dispensation?.id;
                  if (!dispId) return;
                  window.location.href = `/dispensations/${dispId}?tab=suppliers&lab=${encodeURIComponent(lab)}`;
                }}
              >
                {data.suppliers_by_agreement.slice(0, 20).map((s, i) => (
                  <Cell key={i} fill={s.has_agreement ? COLORS.emerald : COLORS.rose} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </>
  );
}

/* ---- subcomponents ---- */

function KpiCard({ icon: Icon, label, value, sub, accent = "slate", hint, cta, href, testid }) {
  const accents = {
    emerald: "from-emerald-50 to-white border-emerald-200 text-emerald-700",
    sky: "from-sky-50 to-white border-sky-200 text-sky-700",
    rose: "from-rose-50 to-white border-rose-200 text-rose-700",
    amber: "from-amber-50 to-white border-amber-200 text-amber-700",
    indigo: "from-indigo-50 to-white border-indigo-200 text-indigo-700",
    slate: "from-slate-50 to-white border-slate-200 text-slate-700",
  };
  const Wrap = href ? "a" : "div";
  const wrapProps = href
    ? {
        href,
        "data-testid": testid,
        title: hint || `Abrir ${label}`,
        className: `block bg-gradient-to-br ${accents[accent]} border rounded-2xl p-5 relative transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 cursor-pointer`,
      }
    : {
        "data-testid": testid,
        title: hint || undefined,
        className: `bg-gradient-to-br ${accents[accent]} border rounded-2xl p-5 relative`,
      };
  return (
    <Wrap {...wrapProps}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 inline-flex items-center gap-1.5">
          {label}
          {hint && (
            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-slate-200 text-slate-600 text-[9px] font-bold cursor-help" title={hint}>
              ?
            </span>
          )}
        </span>
        {Icon && <Icon className={`h-4 w-4 ${accents[accent].split(" ").slice(-1)[0]}`} strokeWidth={1.8} />}
      </div>
      <div className="font-display text-3xl font-extrabold tracking-tight text-slate-950 font-mono-num">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
      {cta && !href && (
        <a
          href={cta.href}
          data-testid={cta.testid}
          className={`mt-3 inline-flex items-center gap-1.5 text-xs font-bold ${accent === "rose" ? "text-rose-700 hover:text-rose-900" : "text-slate-700 hover:text-slate-900"} hover:underline`}
        >
          {cta.label} <span aria-hidden>→</span>
        </a>
      )}
      {href && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 opacity-70">
          Ver detalle <span aria-hidden>→</span>
        </div>
      )}
    </Wrap>
  );
}

/* ---------- Moléculas en caída vs estudio anterior (iter-98) ---------- */

function MoleculesFallingCard({ falling, dispId }) {
  const items = falling?.items || [];
  if (!falling?.has_prev || items.length === 0) return null;
  const worst = Math.min(...items.map((i) => i.delta_pp));
  return (
    <div className="mb-6">
      <Card className="bg-rose-50/40 border border-rose-200" testid="molecules-falling-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle icon={TrendingDown}>Moléculas en caída vs {falling.prev_label || "el estudio anterior"}</CardTitle>
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">
            {items.length} moléculas · peor {worst.toFixed(1)} pp
          </span>
        </div>
        <CardSubtitle>
          Moléculas cuyo % de cambio a genérico ha caído <strong>más de 10 puntos</strong> respecto al estudio anterior,
          ordenadas por <strong>impacto en € de marca</strong>. Son las que más dinero te cuestan: recupéralas primero.
        </CardSubtitle>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="molecules-falling-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-rose-200">
              <tr>
                <th className="text-left py-2">Molécula (G.H.)</th>
                <th className="text-right py-2">Uds. totales</th>
                <th className="text-right py-2">Antes</th>
                <th className="text-right py-2">Ahora</th>
                <th className="text-right py-2">Caída</th>
                <th className="text-right py-2">Marca €</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rose-100">
              {items.map((m) => (
                <tr key={m.gh} className="hover:bg-white/70" data-testid={`molecules-falling-row-${m.gh}`}>
                  <td className="py-2 max-w-[320px]">
                    <span className="block truncate font-medium text-slate-900" title={m.gh}>{m.gh}</span>
                  </td>
                  <td className="py-2 text-right font-mono-num">{fmtNum(m.total_units)}</td>
                  <td className="py-2 text-right font-mono-num text-slate-500">{fmtPct(m.pct_prev)}</td>
                  <td className="py-2 text-right font-mono-num font-semibold">{fmtPct(m.pct_now)}</td>
                  <td className="py-2 text-right font-mono-num font-bold text-rose-700">{m.delta_pp.toFixed(1)} pp</td>
                  <td className="py-2 text-right font-mono-num">{fmtEur(m.marca_euros)}</td>
                  <td className="py-2 text-right">
                    {dispId && (
                      <a
                        href={`/dispensations/${dispId}?tab=molecules&gh=${encodeURIComponent(m.gh)}`}
                        data-testid={`molecules-falling-link-${m.gh}`}
                        className="text-[11px] font-bold text-rose-800 hover:text-rose-950 hover:underline whitespace-nowrap"
                        title="Abrir esta molécula en el estudio"
                      >
                        Ver →
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Card({ children, className = "", testid, href, hrefLabel }) {
  if (href) {
    return (
      <a
        href={href}
        data-testid={testid}
        title={hrefLabel || "Abrir vista detallada"}
        className={`block bg-white border border-slate-200 rounded-2xl p-5 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 ${className}`}
      >
        {children}
        <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-slate-900">
          {hrefLabel || "Ver detalle"} <span aria-hidden>→</span>
        </div>
      </a>
    );
  }
  return <div data-testid={testid} className={`bg-white border border-slate-200 rounded-2xl p-5 ${className}`}>{children}</div>;
}
function CardTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 text-slate-900 font-semibold">
      {Icon && <Icon className="h-4 w-4" strokeWidth={1.8} />}
      {children}
    </div>
  );
}
function CardSubtitle({ children }) {
  return <div className="text-xs text-slate-500 mt-1 mb-3">{children}</div>;
}
function EmptyChart({ children }) {
  return <div className="text-center text-slate-400 py-12 italic text-sm">{children}</div>;
}
function LegendRow({ color, label, value, pct }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <div className="flex items-center gap-2">
        <span className="h-3 w-3 rounded-sm" style={{ background: color }} />
        <span className="font-medium text-slate-700">{label}</span>
      </div>
      <div className="text-right">
        <span className="font-mono-num font-semibold text-slate-900">{value}</span>
        <span className="text-slate-500 ml-2 font-mono-num">{pct}%</span>
      </div>
    </div>
  );
}
function EmployeeCard({ variant, emp }) {
  if (!emp) return null;
  const isBest = variant === "best";
  return (
    <div className={`rounded-xl p-4 border ${isBest ? "bg-emerald-50/60 border-emerald-200" : "bg-rose-50/60 border-rose-200"}`} data-testid={`employee-${variant}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {isBest ? <Trophy className="h-4 w-4 text-emerald-600" /> : <Frown className="h-4 w-4 text-rose-600" />}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{isBest ? "Mejor empleado" : "Peor empleado"}</div>
            <div className="font-semibold text-slate-900 truncate">{emp.name}</div>
          </div>
        </div>
        <div className="text-right">
          <div className={`font-display text-2xl font-extrabold font-mono-num ${isBest ? "text-emerald-700" : "text-rose-700"}`}>{fmtPct(emp.pct_eur)}</div>
          <div className="text-[10px] text-slate-500 font-mono-num">% gen €</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        <Mini label="% Gen. uds" value={fmtPct(emp.pct_units)} />
        <Mini label="Unidades" value={fmtNum(emp.units_total)} />
        <Mini label="Total €" value={fmtEur(emp.eur_total)} />
      </div>
    </div>
  );
}
function Mini({ label, value }) {
  return (
    <div className="bg-white/70 border border-slate-200 rounded-lg py-1.5">
      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-xs font-bold text-slate-900 font-mono-num">{value}</div>
    </div>
  );
}

function BenchmarkSummaryCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get("/benchmark/provinces")
      .then((r) => {
        if (mounted) setData(r.data);
      })
      .catch(() => mounted && setData(null))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) return null;
  const sp = data?.self_position;
  if (!sp) return null;

  const renderRank = (rank, total, percentile, label, hint) => {
    const isTopHalf = (percentile ?? 0) >= 50;
    const color = (percentile ?? 0) >= 75
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : (percentile ?? 0) >= 50
        ? "text-sky-700 bg-sky-50 border-sky-200"
        : (percentile ?? 0) >= 25
          ? "text-amber-700 bg-amber-50 border-amber-200"
          : "text-rose-700 bg-rose-50 border-rose-200";
    return (
      <div className={`rounded-xl border p-4 ${color}`}>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1 opacity-80">{label}</div>
        <div className="flex items-baseline gap-2">
          <div className="font-display text-3xl font-extrabold font-mono-num leading-none">#{rank || "—"}</div>
          <div className="text-[11px] font-mono-num opacity-70">de {total || "—"}</div>
        </div>
        <div className="text-[11px] mt-1 font-mono-num">
          Percentil <strong>{percentile != null ? `${percentile.toFixed(1)}%` : "—"}</strong>
          {percentile != null && (
            <span className="ml-1 opacity-70">
              {percentile >= 90 ? "· Top 10%" : percentile >= 75 ? "· Top 25%" : percentile >= 50 ? "· Top 50%" : ""}
            </span>
          )}
        </div>
        <div className="text-[10px] mt-1 opacity-70">{hint}</div>
      </div>
    );
  };

  return (
    <div
      data-testid="benchmark-summary-card"
      className="mb-6 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Resumen Benchmark</div>
          <div className="font-display text-lg font-bold text-slate-900">
            Tu posición frente a otras farmacias
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Tu % genérico (€): <strong className="text-slate-900 font-mono-num">{fmtPct(sp.self_generic_pct)}</strong>
            {sp.ccaa_avg_generic_pct != null && (
              <> · Media {sp.ccaa}: <span className="font-mono-num">{fmtPct(sp.ccaa_avg_generic_pct)}</span></>
            )}
          </div>
        </div>
        <a href="/benchmark" data-testid="bm-view-detail" className="text-xs font-bold text-sky-700 hover:text-sky-900 underline">
          Ver detalle →
        </a>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {renderRank(
          sp.ccaa_rank, sp.ccaa_studies, sp.ccaa_percentile,
          `${sp.ccaa || "Tu CCAA"}`,
          `Posición entre las farmacias de ${sp.ccaa || "tu CCAA"} cargadas en FarmaClass`,
        )}
        {renderRank(
          sp.national_rank, sp.national_universe, sp.national_percentile,
          "Nacional",
          "Posición entre todas las farmacias españolas registradas",
        )}
      </div>
    </div>
  );
}

function StockDeadCard({ dead: initialDead, dispId }) {
  const [monthsWindow, setMonthsWindow] = useState(0);
  const [dead, setDead] = useState(initialDead);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDead(initialDead); }, [initialDead]);

  // Modal de detalle por laboratorio
  const [labModal, setLabModal] = useState(null); // {laboratorio, has_agreement} | null
  const [labDetail, setLabDetail] = useState(null);
  const [labBusy, setLabBusy] = useState(false);

  const openLabModal = async (lab) => {
    setLabModal({ laboratorio: lab.laboratorio, has_agreement: lab.has_agreement });
    setLabDetail(null);
    setLabBusy(true);
    try {
      const params = monthsWindow > 0 ? { dead_window_months: monthsWindow } : {};
      const r = await api.get(`/dashboard/stock-dead/lab/${encodeURIComponent(lab.laboratorio)}`, { params });
      setLabDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el detalle del laboratorio");
      setLabModal(null);
    } finally {
      setLabBusy(false);
    }
  };
  const closeLabModal = () => { setLabModal(null); setLabDetail(null); };
  const labExportUrl = (fmt) => {
    if (!labModal) return "";
    const win = monthsWindow > 0 ? `&dead_window_months=${monthsWindow}` : "";
    return `/dashboard/stock-dead/lab/${encodeURIComponent(labModal.laboratorio)}/export?format=${fmt}${win}`;
  };

  useEffect(() => {
    if (monthsWindow === 0 || !dispId) return;
    setBusy(true);
    api.get("/dashboard/executive", { params: { disp_id: dispId, dead_window_months: monthsWindow } })
      .then((r) => setDead(r.data?.stock_dead || null))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [monthsWindow, dispId]);

  if (!dead || (dead.total_cn === 0 && (monthsWindow || 0) === 0)) return null;
  const opts = [
    { v: 0, label: "Todo" },
    { v: 3, label: "3m" },
    { v: 6, label: "6m" },
    { v: 12, label: "12m" },
  ];
  const exportUrl = `/dashboard/stock-dead/export${monthsWindow > 0 ? `?dead_window_months=${monthsWindow}` : ""}`;
  const pctNoAgr = dead.total_eur_value > 0
    ? (dead.without_agreement_eur / dead.total_eur_value * 100)
    : 0;
  return (
    <div data-testid="stock-dead-card" className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      <Card testid="stock-dead-kpi" className="bg-orange-50/60 border border-orange-200">
        <div className="flex items-center justify-between gap-3">
          <CardTitle icon={PackageX}>
            {monthsWindow > 0 ? `Stock sin ventas en los últimos ${monthsWindow} meses` : "Stock sin ventas en el histórico"}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-full bg-white border border-orange-200 p-0.5" data-testid="dead-window-toggle">
              {opts.map((o) => (
                <button
                  key={o.v}
                  onClick={() => setMonthsWindow && setMonthsWindow(o.v)}
                  data-testid={`dead-window-${o.v}`}
                  className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full transition ${
                    monthsWindow === o.v ? "bg-orange-600 text-white" : "text-orange-700 hover:bg-orange-50"
                  }`}
                  title={o.v === 0 ? "Considera ventas de cualquier estudio cargado" : `Sólo cuenta ventas de los últimos ${o.v} meses`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => downloadXlsx(exportUrl, "stock_muerto.xlsx")}
              title="Descargar listado completo a Excel"
              data-testid="stock-dead-export"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-1 rounded-full bg-white border border-orange-300 text-orange-800 hover:bg-orange-100"
            >
              <Download className="h-3 w-3" strokeWidth={2} /> Excel
            </button>
          </div>
        </div>
        <CardSubtitle>
          Stock <strong>genérico</strong> y stock de laboratorios <strong>con condición</strong> que tienes en estantería pero <strong>0 unidades dispensadas</strong>
          {monthsWindow > 0
            ? <> en los <strong>últimos {monthsWindow} meses</strong> (sólo estudios cuyo periodo termina en esa ventana).</>
            : <> en <strong>ninguno de tus {dead.studies_count > 1 ? `${dead.studies_count} estudios` : "estudios"}</strong> cargados.</>
          } El resto del stock (marcas sin condición) no se contabiliza aquí.
        </CardSubtitle>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="rounded-xl p-4 border border-orange-200 bg-white">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">CN sin venta</div>
            <div className="font-display text-3xl font-extrabold text-orange-700 font-mono-num" data-testid="stock-dead-count">
              {fmtNum(dead.total_cn)}
            </div>
            <div className="text-[11px] text-slate-500 font-mono-num mt-1">{fmtNum(dead.total_units)} uds inmovilizadas</div>
          </div>
          <div className="rounded-xl p-4 border border-orange-200 bg-white">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">€ inmovilizado</div>
            <div className="font-display text-3xl font-extrabold text-orange-700 font-mono-num" data-testid="stock-dead-eur">
              {fmtEur(dead.total_eur_value)}
            </div>
            <div className="text-[11px] text-slate-500 font-mono-num mt-1">Valoración a PVL del catálogo</div>
          </div>
        </div>
        <a
          href="/stock"
          data-testid="stock-dead-link"
          className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-orange-800 hover:text-orange-950 hover:underline"
        >
          Ver stock completo <span aria-hidden>→</span>
        </a>
      </Card>

      <Card className="lg:col-span-2 bg-rose-50/40 border border-rose-200" testid="stock-dead-without-agreement">
        <div className="flex items-center justify-between gap-3">
          <CardTitle icon={AlertTriangle}>De ese stock, sin acuerdo comercial</CardTitle>
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">
            Sin condición
          </span>
        </div>
        <CardSubtitle>
          De los {fmtNum(dead.total_cn)} CN con stock sin venta, estos son de laboratorios <strong>sin acuerdo comercial</strong>.
          Mercancía dormida que no te beneficia ni en margen ni en rappel.
        </CardSubtitle>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <div className="rounded-xl p-3 border border-rose-200 bg-white">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">CN sin condición</div>
            <div className="font-display text-2xl font-extrabold text-rose-700 font-mono-num" data-testid="stock-dead-without-cn">
              {fmtNum(dead.without_agreement_cn)}
            </div>
            <div className="text-[11px] text-slate-500 font-mono-num mt-1">
              {dead.total_cn ? `${((dead.without_agreement_cn / dead.total_cn) * 100).toFixed(0)}% del total muerto` : ""}
            </div>
          </div>
          <div className="rounded-xl p-3 border border-rose-200 bg-white">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">€ sin condición</div>
            <div className="font-display text-2xl font-extrabold text-rose-700 font-mono-num">{fmtEur(dead.without_agreement_eur)}</div>
            <div className="text-[11px] text-slate-500 font-mono-num mt-1">{pctNoAgr.toFixed(0)}% del valor muerto</div>
          </div>
          <div className="rounded-xl p-3 border border-rose-200 bg-white">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Unidades sin condición</div>
            <div className="font-display text-2xl font-extrabold text-rose-700 font-mono-num">{fmtNum(dead.without_agreement_units)}</div>
            <div className="text-[11px] text-slate-500 font-mono-num mt-1">A reasignar o devolver</div>
          </div>
        </div>
        {dead.labs && dead.labs.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Top laboratorios con stock muerto</div>
            <div className="space-y-1 max-h-44 overflow-y-auto">
              {dead.labs.slice(0, 8).map((l, idx) => (
                <button
                  key={`${l.laboratorio}-${idx}`}
                  type="button"
                  onClick={() => openLabModal(l)}
                  data-testid={`stock-dead-lab-row-${idx}`}
                  className="w-full flex items-center justify-between gap-3 px-2 py-1.5 bg-white border border-slate-100 rounded hover:border-rose-300 hover:bg-rose-50/50 transition text-left group"
                  title={`Ver detalle de ${l.laboratorio}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full whitespace-nowrap ${l.has_agreement ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                      {l.has_agreement ? "Con cond." : "Sin cond."}
                    </span>
                    <span className="text-xs font-medium text-slate-900 truncate group-hover:text-rose-900" title={l.laboratorio}>{l.laboratorio}</span>
                  </div>
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <div className="text-right">
                      <div className="text-[11px] font-mono-num font-bold text-slate-900">{fmtEur(l.eur_value)}</div>
                      <div className="text-[10px] font-mono-num text-slate-500">{fmtNum(l.cn_count)} CN · {fmtNum(l.units)} uds</div>
                    </div>
                    <span className="text-[10px] text-slate-300 group-hover:text-rose-500 transition" aria-hidden>→</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        <a
          href="/stock"
          data-testid="stock-without-agreement-link"
          className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-rose-800 hover:text-rose-950 hover:underline"
        >
          Ver y filtrar stock sin condición <span aria-hidden>→</span>
        </a>
      </Card>

      {labModal && (
        <StockDeadLabModal
          laboratorio={labModal.laboratorio}
          hasAgreement={labModal.has_agreement}
          detail={labDetail}
          busy={labBusy}
          onClose={closeLabModal}
          onExport={(fmt) => downloadXlsx(labExportUrl(fmt), `stock_muerto_${labModal.laboratorio.replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 40)}.${fmt === "pdf" ? "pdf" : "xlsx"}`)}
        />
      )}
    </div>
  );
}

function StockDeadLabModal({ laboratorio, hasAgreement, detail, busy, onClose, onExport }) {
  return (
    <div
      data-testid="stock-dead-lab-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <PackageX className="h-4 w-4 text-rose-600" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Stock muerto · Laboratorio</span>
              <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${hasAgreement ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                {hasAgreement ? "Con condición" : "Sin condición"}
              </span>
            </div>
            <h2 className="font-display text-xl font-extrabold text-slate-900 truncate" data-testid="stock-dead-lab-title">{laboratorio}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onExport("xlsx")}
              data-testid="stock-dead-lab-export-xlsx"
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-900 px-3 py-2 rounded-md text-xs font-medium hover:bg-slate-50"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
            </button>
            <button
              onClick={() => onExport("pdf")}
              data-testid="stock-dead-lab-export-pdf"
              className="inline-flex items-center gap-1.5 bg-rose-700 text-white px-3 py-2 rounded-md text-xs font-medium hover:bg-rose-800"
            >
              <FileText className="h-3.5 w-3.5" /> PDF
            </button>
            <button
              onClick={onClose}
              data-testid="stock-dead-lab-modal-close"
              className="ml-2 text-slate-400 hover:text-slate-700 text-2xl leading-none px-2"
              aria-label="Cerrar"
            >×</button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-6">
          {busy && (
            <div className="flex items-center justify-center py-20 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Cargando detalle…
            </div>
          )}
          {!busy && detail && (
            <>
              <div className="grid grid-cols-4 gap-3 mb-5">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">CN dormidos</div>
                  <div className="font-mono-num text-2xl font-extrabold text-rose-700">{fmtNum(detail.totals?.cn_count)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Uds en stock</div>
                  <div className="font-mono-num text-2xl font-extrabold text-rose-700">{fmtNum(detail.totals?.stock_units)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">€ inmovilizado</div>
                  <div className="font-mono-num text-2xl font-extrabold text-rose-700">{fmtEur(detail.totals?.eur_stock)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Uds vendidas histórico</div>
                  <div className="font-mono-num text-2xl font-extrabold text-emerald-700">{fmtNum(detail.totals?.historical_units_total)}</div>
                </div>
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm" data-testid="stock-dead-lab-table">
                  <thead className="bg-slate-900 text-white">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">CN</th>
                      <th className="px-3 py-2 text-left font-semibold">Producto</th>
                      <th className="px-3 py-2 text-left font-semibold">G.H.</th>
                      <th className="px-3 py-2 text-right font-semibold">Uds stock</th>
                      <th className="px-3 py-2 text-right font-semibold">PVL</th>
                      <th className="px-3 py-2 text-right font-semibold">€ inmov.</th>
                      <th className="px-3 py-2 text-left font-semibold">Última venta</th>
                      <th className="px-3 py-2 text-left font-semibold">Histórico (estudios → uds)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(detail.items || []).map((r) => (
                      <tr key={r.cn} className="odd:bg-slate-50/50">
                        <td className="px-3 py-2 font-mono-num text-slate-700">{r.cn}</td>
                        <td className="px-3 py-2 text-slate-900">{r.name}</td>
                        <td className="px-3 py-2 text-slate-500 text-xs">{r.gh}</td>
                        <td className="px-3 py-2 text-right font-mono-num font-bold text-rose-700">{fmtNum(r.stock_units)}</td>
                        <td className="px-3 py-2 text-right font-mono-num text-slate-700">{Number(r.pvl).toFixed(2)} €</td>
                        <td className="px-3 py-2 text-right font-mono-num font-bold text-slate-900">{fmtEur(r.eur_stock)}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.last_sale_label}</td>
                        <td className="px-3 py-2 text-xs">
                          {r.history && r.history.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {r.history.slice(0, 4).map((h, i) => (
                                <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-mono-num">
                                  {h.label}: <strong>{h.units}u</strong>
                                </span>
                              ))}
                              {r.history.length > 4 && (
                                <span className="text-slate-400">+{r.history.length - 4}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 italic">Sin ventas</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 mt-3 italic">
                La columna <strong>Histórico</strong> muestra los estudios donde se vendió cada CN y la cantidad de unidades. Si todas las celdas están vacías, el producto nunca se ha dispensado — candidato a <strong>devolución</strong>. Si tiene histórico, valora la rotación antes de decidir.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ArchiduqueBenefitCard({ benefit, studies = [], currentDispId = null, onSelect = null }) {
  // Ordenamos por period_start ASC (o created_at si falta) para que las
  // flechas de navegación sigan el mismo orden cronológico que el timeline.
  const sorted = React.useMemo(() => {
    return [...studies].sort((a, b) => {
      const ka = a.period_start || a.created_at || "";
      const kb = b.period_start || b.created_at || "";
      return ka < kb ? -1 : ka > kb ? 1 : 0;
    });
  }, [studies]);
  if (!benefit) return null;
  const b = benefit;
  const idx = sorted.findIndex((s) => s.id === currentDispId);
  const canPrev = idx > 0;
  const canNext = idx >= 0 && idx < sorted.length - 1;
  const goPrev = () => canPrev && onSelect && onSelect(sorted[idx - 1].id);
  const goNext = () => canNext && onSelect && onSelect(sorted[idx + 1].id);
  return (
    <div
      data-testid="archiduque-benefit-card"
      className="mb-6 rounded-2xl overflow-hidden border border-emerald-300 shadow-md bg-gradient-to-br from-emerald-50 via-white to-emerald-50"
    >
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-5 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          <span className="text-[11px] uppercase tracking-[0.22em] font-bold">Mejora con Archiduque Consulting</span>
        </div>
        {onSelect && sorted.length >= 2 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={goPrev}
              disabled={!canPrev}
              data-testid="archiduque-prev-study"
              title={canPrev ? `Ver estudio anterior · ${sorted[idx - 1].label ?? "estudio"}` : "Ya estás en el primer estudio"}
              className="p-1.5 rounded-md text-white/90 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-white/80 px-1 min-w-[46px] text-center" data-testid="archiduque-position">
              {idx >= 0 ? `${idx + 1} / ${sorted.length}` : `— / ${sorted.length}`}
            </span>
            <button
              type="button"
              onClick={goNext}
              disabled={!canNext}
              data-testid="archiduque-next-study"
              title={canNext ? `Ver estudio siguiente · ${sorted[idx + 1].label ?? "estudio"}` : "Ya estás en el último estudio"}
              className="p-1.5 rounded-md text-white/90 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      <div className="p-5 lg:p-6 grid grid-cols-1 lg:grid-cols-3 gap-5 items-center">
        <div className="lg:col-span-1">
          <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-1">Beneficio estimado acumulado</div>
          <div className="font-display text-5xl font-extrabold text-emerald-700 font-mono-num leading-none" data-testid="archiduque-total-eur">
            {fmtEur(b.total_benefit_eur)}
          </div>
          <div className="text-sm text-slate-600 mt-2 leading-relaxed">
            Desde <strong>{b.baseline_label}</strong> hasta <strong>{b.current_label}</strong>: tu cuota de genérico en € pasó del{" "}
            <strong className="text-slate-900">{fmtPct(b.baseline_pct_eur)}</strong> al{" "}
            <strong className="text-emerald-700">{fmtPct(b.current_pct_eur)}</strong>{" "}
            <span className="text-emerald-700 font-semibold">(+{b.improvement_pp.toFixed(1)} pp)</span>.
          </div>
        </div>

        <div className="lg:col-span-2 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MiniStat label="Mejora % gen €" value={`+${b.improvement_pp.toFixed(1)} pp`} accent="emerald" testid="archiduque-improvement" />
          <MiniStat
            label="€ medio/mes"
            value={fmtEur(b.avg_eur_total_per_month)}
            sub={`Margen ${b.margin_pct}% sobre genérico`}
            testid="archiduque-avg-month"
          />
          <MiniStat
            label="Beneficio mensual"
            value={fmtEur(b.monthly_benefit_eur)}
            sub={`× ${b.months_studied} mes${b.months_studied !== 1 ? "es" : ""} cargados`}
            accent="emerald"
            testid="archiduque-monthly-benefit"
          />
          <MiniStat
            label="Proyección"
            value={b.projected_extra_months > 0 ? fmtEur(b.projected_extra_benefit_eur) : "—"}
            sub={
              b.projected_extra_months > 0
                ? `+${b.projected_extra_months} mes${b.projected_extra_months !== 1 ? "es" : ""} hasta hoy`
                : "Periodo cubierto íntegro"
            }
            testid="archiduque-projection"
          />
        </div>
      </div>
      <div className="px-5 lg:px-6 pb-4 -mt-1">
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer hover:text-slate-700 font-medium">¿Cómo se calcula?</summary>
          <p className="mt-2 leading-relaxed bg-white/60 border border-emerald-100 rounded-lg p-3">
            Beneficio mensual = mejora del % de genérico (en €) × € medio/mes × margen del {b.margin_pct}% sobre genérico. Multiplicamos
            por los meses cubiertos por tus cargas ({b.months_studied}) y proyectamos a los meses transcurridos desde el primer estudio hasta hoy
            ({b.months_elapsed_total}). Si has pasado meses sin cargar dispensación, se asume que has mantenido la mejora.
          </p>
        </details>
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, accent = "slate", testid }) {
  const accents = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
  };
  return (
    <div className="bg-white/80 border border-emerald-100 rounded-xl p-3" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`font-display text-lg font-extrabold font-mono-num ${accents[accent] || accents.slate} leading-tight mt-0.5`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function PendingToConvertCard({ gap, dispLabel, dispId }) {
  if (!gap) return null;
  // Ambos modos ("stock" y "solo_marca") traen solo_marca_euros / total_marca_euros / solo_marca_count.
  const soloEur = Number(gap.solo_marca_euros || 0);
  const marcaEur = Number(gap.total_marca_euros || 0);
  const molecs = Number(gap.solo_marca_count || 0);
  // Aparecer SIEMPRE que haya al menos moléculas SOLO MARCA (aunque el % sobre
  // total marca sea 0 por falta de datos temporales). Si no hay ninguna
  // molécula solo_marca, mostramos igual una card informativa positiva.
  const hasSoloMarca = molecs > 0 && soloEur > 0;
  const ratio = marcaEur > 0
    ? (Number(gap.pct_over_total_marca) || (soloEur / marcaEur * 100))
    : 0;
  const target = dispId ? `/dispensations/${dispId}?tab=molecules&state=solo_marca` : "/history";

  if (!hasSoloMarca) {
    return (
      <div
        data-testid="kpi-solo-marca-ratio"
        className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-6 flex items-center gap-4"
      >
        <Target className="h-6 w-6 text-emerald-600" strokeWidth={2} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-1">
            Pendiente de convertir · {dispLabel || "estudio"}
          </div>
          <div className="text-sm text-slate-700">
            <strong>¡Sin moléculas SOLO MARCA sin sustituir!</strong> Todo el volumen EFG dispensado en marca ya tiene su equivalente genérico en marcha.
          </div>
        </div>
      </div>
    );
  }

  return (
    <a
      href={target}
      data-testid="kpi-solo-marca-ratio"
      className="block bg-gradient-to-br from-slate-950 to-slate-800 text-white rounded-lg p-6 mb-6 border border-slate-900 hover:from-slate-900 hover:to-slate-800 transition-colors"
    >
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300 mb-3">
            <Target className="h-3 w-3" strokeWidth={2} />
            Pendiente de convertir · {dispLabel || "estudio"}
          </div>
          <div className="flex items-baseline gap-4 flex-wrap">
            <span className="font-display text-6xl font-bold tracking-tighter font-mono-num">
              {ratio.toFixed(1).replace(".", ",")}%
            </span>
            <div className="text-sm text-slate-300 max-w-md">
              del volumen de marca dispensada está en moléculas{" "}
              <strong className="text-white">SOLO MARCA</strong> (sin sustitución a genérico iniciada).
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-x-6 gap-y-1 text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">€ Solo Marca</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">€ Marca total</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Moléculas</div>
          <div className="font-mono-num font-bold text-rose-300">{fmtEur(soloEur)}</div>
          <div className="font-mono-num font-bold">{fmtEur(marcaEur)}</div>
          <div className="font-mono-num font-bold">{molecs}</div>
        </div>
      </div>
      <div className="mt-4 h-2 w-full bg-slate-700 rounded-full overflow-hidden">
        <div
          className="bg-rose-500 h-full transition-all"
          style={{ width: `${Math.min(100, ratio)}%` }}
        />
      </div>
    </a>
  );
}

function StockGapCard({ gap, totalMarcaEur, dispId, dispLabel }) {
  if (!gap) return null;
  const isStock = gap.mode === "stock";
  const headline = isStock ? gap.molecules_without_stock : gap.solo_marca_count;
  const accentBg = isStock ? "bg-amber-50/60" : "bg-rose-50/60";
  const accentBorder = isStock ? "border-amber-200" : "border-rose-200";
  const accentText = isStock ? "text-amber-700" : "text-rose-700";
  const Icon = isStock ? PackageX : PackageOpen;

  const title = isStock
    ? "Moléculas sin stock de genérico"
    : "Moléculas en SOLO MARCA";
  const subtitle = isStock
    ? `De las ${(gap.molecules_dispensed ?? 0).toLocaleString("es-ES")} G.H. dispensadas, ${headline.toLocaleString("es-ES")} no tienen ni una unidad del genérico equivalente en tu stock (${fmtPct(gap.pct_without_stock)}).`
    : `Sube tu stock para detectar roturas en tiempo real. Mientras tanto, estas son las G.H. que sólo dispensas en marca — el genérico está totalmente disponible para sustituirlas.`;

  const top = gap.top || [];

  const downloadExport = async (format) => {
    try {
      const r = await api.get("/dashboard/stock-gap/export", {
        params: { format, ...(dispId ? { disp_id: dispId } : {}) },
        responseType: "blob",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([r.data]));
      const ext = format === "pdf" ? "pdf" : "xlsx";
      link.download = `pedido-${isStock ? "sin-stock" : "solo-marca"}-${(dispLabel || "estudio").replace(/\s+/g, "_")}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error("Error al exportar");
    }
  };

  const composeEmail = () => {
    const heads = top.slice(0, 5)
      .map((m, i) => `${i + 1}. ${m.gh} → ${fmtEur(m.marca_euros)} (${m.marca_units} uds${m.preferred_lab ? ` · ${m.preferred_lab}` : ""})`)
      .join("\n");
    const totalEur = isStock ? gap.marca_euros_at_risk : gap.solo_marca_euros;
    const subject = isStock
      ? `Pedido urgente · ${headline} G.H. sin stock`
      : `Solicitud sustitución a EFG · ${headline} G.H. Solo Marca`;
    const body = `Buenos días,\n\nOs adjunto el listado de moléculas ${isStock ? "que actualmente no tenemos en stock y necesitamos reponer" : "que estamos dispensando exclusivamente en marca y queremos sustituir a EFG"}.\n\nTotal: ${headline} G.H. · ${fmtEur(totalEur)} en € marca\n\nTop:\n${heads}\n\nAdjunto Excel/PDF con el detalle completo.\n\nUn saludo.\n\n— FarmaClass`;
    const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      <Card testid={`stock-gap-card-${gap.mode}`} className={`${accentBg} border ${accentBorder}`}>
        <div className="flex items-center justify-between gap-3">
          <CardTitle icon={Icon}>{title}</CardTitle>
          <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${isStock ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>
            {isStock ? "Stock subido" : "Sin stock subido"}
          </span>
        </div>
        <CardSubtitle>{subtitle}</CardSubtitle>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className={`rounded-xl p-4 border ${accentBorder} bg-white`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              {isStock ? "G.H. sin stock" : "G.H. solo marca"}
            </div>
            <div className={`font-display text-3xl font-extrabold font-mono-num ${accentText}`} data-testid="stock-gap-count">
              {(headline || 0).toLocaleString("es-ES")}
            </div>
            <div className="text-[11px] text-slate-500 font-mono-num mt-1">
              {isStock
                ? `${fmtPct(gap.pct_without_stock)} sobre dispensadas`
                : `${fmtNum(gap.solo_marca_units)} uds marca`}
            </div>
          </div>
          <div className={`rounded-xl p-4 border ${accentBorder} bg-white`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              {isStock ? "€ marca en riesgo" : "€ marca · Solo Marca"}
            </div>
            <div className={`font-display text-3xl font-extrabold font-mono-num ${accentText}`} data-testid="stock-gap-euros">
              {fmtEur(isStock ? gap.marca_euros_at_risk : gap.solo_marca_euros)}
            </div>
            <div className="text-[11px] text-slate-500 font-mono-num mt-1">
              {isStock
                ? `${fmtNum(gap.marca_units_at_risk)} uds marca afectadas`
                : `${fmtPct(gap.pct_over_total_marca)} sobre € marca total (${fmtEur(gap.total_marca_euros || totalMarcaEur)})`}
            </div>
          </div>
        </div>
        {(headline || 0) > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {!isStock && (
              <a
                href="/stock"
                data-testid="stock-gap-go-stock"
                className="inline-flex items-center gap-1.5 bg-white hover:bg-amber-50 border border-amber-300 text-amber-800 text-xs font-bold px-3 py-2 rounded-md"
              >
                Subir stock <span aria-hidden>→</span>
              </a>
            )}
            <button
              type="button"
              onClick={() => downloadExport("excel")}
              data-testid="stock-gap-export-excel"
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-md"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              Excel
            </button>
            <button
              type="button"
              onClick={() => downloadExport("pdf")}
              data-testid="stock-gap-export-pdf"
              className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-3 py-2 rounded-md"
            >
              <FileText className="h-3.5 w-3.5" />
              PDF
            </button>
            <button
              type="button"
              onClick={composeEmail}
              data-testid="stock-gap-email"
              className="inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold px-3 py-2 rounded-md"
            >
              <Mail className="h-3.5 w-3.5" />
              Enviar al mayorista
            </button>
          </div>
        )}
      </Card>

      <Card className="lg:col-span-2" testid="stock-gap-top" href="/sin-stock-genericos" hrefLabel={isStock ? "Ver listado completo de moléculas sin stock" : "Ver listado completo de moléculas Solo Marca"}>
        <CardTitle icon={AlertTriangle}>
          {isStock ? "Top G.H. sin stock con más € marca" : "Top G.H. Solo Marca por € convertible"}
        </CardTitle>
        <CardSubtitle>
          {isStock
            ? "Empieza pidiendo estas: las dispensas pero no tienes ni una caja en la estantería."
            : "Estas moléculas se dispensan exclusivamente en marca — todas pueden cambiarse a EFG sin esfuerzo."}
        </CardSubtitle>
        {top.length === 0 ? (
          <EmptyChart>
            {isStock
              ? "Perfecto: tienes al menos 1 unidad en stock de todas las G.H. que dispensas."
              : "Sin moléculas en estado SOLO MARCA. ¡Trabajo impecable!"}
          </EmptyChart>
        ) : (
          <div className="space-y-2 mt-3">
            {top.map((m, idx) => (
              <div key={`${m.gh}-${idx}`} className={`${accentBg} border ${accentBorder} rounded-lg p-3`} data-testid={`stock-gap-row-${idx}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className={`text-[10px] uppercase tracking-wider font-bold ${accentText} flex items-center gap-1.5`}>
                      <span className={`font-mono-num ${isStock ? "bg-amber-100" : "bg-rose-100"} px-1.5 rounded-full`}>#{idx + 1}</span>
                      {m.state_label || (isStock ? "Sin stock" : "Solo Marca")}
                      {m.preferred_lab && (
                        <span className="text-[9px] text-slate-500 normal-case font-normal">· → {m.preferred_lab}</span>
                      )}
                    </div>
                    <div className="text-sm font-medium text-slate-900 truncate" title={m.gh}>{m.gh}</div>
                  </div>
                  <div className="text-right">
                    <div className={`font-display text-lg font-bold font-mono-num whitespace-nowrap ${accentText}`}>{fmtEur(m.marca_euros)}</div>
                    <div className="text-[11px] text-slate-500 font-mono-num">{fmtNum(m.marca_units)} uds</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {!isStock && (
          <span
            data-testid="stock-gap-upload-cta"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-amber-700"
          >
            Sube tu stock para detectar roturas exactas <span aria-hidden>→</span>
          </span>
        )}
      </Card>
    </div>
  );
}
